// 計算ビュー（§9）。左=元帳（入力・表ベース）／右=判定（出力）。
// 行モデル: アイテム名（カタログ選択 or 手入力）｜価値（自動=相場/手動=入力）｜単位（オーブ）｜個数/期待値。
// λ推定は行ごとに畳んで展開。相場の取得時刻はヘッダに1箇所だけ表示する。
import { FRESHNESS_WARN_MINUTES, ROUGH_K_THRESHOLD, STRENGTH_M } from '../../config';
import { fromBase, toBase } from '../../currency/convert';
import { expectedIncome, minIncome } from '../../core/engine';
import { resolveRate } from '../../core/rate-model';
import type { Strength } from '../../core/types';
import { toDisplayUnit } from '../../model/derive';
import { expandSet } from '../../model/expand';
import type { CostLine, DropLine, GroupDrop, RateMode } from '../../store/schema';
import { isGroupDrop } from '../../store/schema';

import { createCostLine, createDropLine, createGroupDrop } from '../../store/factory';
import type { AppStore } from '../state';
import { el, mount } from '../dom';
import { clockHHMM, fmtMoney, fmtNum, pct } from '../format';
import { buildCatalogDatalist, itemNameInput, num, unitCell, unitSelect, valueCell } from '../components/item-picker';
import type { PickerDeps } from '../components/item-picker';
import { RuinChart } from '../components/ruin-chart';
import { judge } from '../components/verdict';

export interface CalcCallbacks {
  onLeagueChange(league: string): void;
  onRefetchRates(): void;
  onSave(): void;
  onLoad(id: string): void;
  onNew(): void;
  onDuplicate(): void;
  onDelete(): void;
  onExport(): void;
  onImport(file: File): void;
  savedSets(): Array<{ id: string; name: string }>;
}

const DATALIST_ID = 'poe2-catalog';

function syncDropCount(drop: DropLine): void {
  drop.count = drop.rateMode.kind === 'direct' ? drop.rateMode.lambda : resolveRate(drop.rateMode).lambdaHat;
}

export class CalcView {
  private ledger!: HTMLElement;
  private verdict!: HTMLElement;
  private chart: RuinChart | null = null;
  private lastInputsKey = '';
  private readonly expanded = new Set<string>();
  private bankrollInput: HTMLInputElement | null = null;
  // ドロップ行の「期待値λ」入力を drop.id で引く（実績パラメタのライブ更新で値だけ書き換える）。
  private readonly dropCountInputs = new Map<string, HTMLInputElement>();
  // アイテム名/価値/単位セル（共通コンポーネント）への依存注入。
  private readonly picker: PickerDeps = {
    rt: () => this.store.rateTable,
    onMutate: (fn) => this.store.updateSet(fn),
    onStructure: () => this.renderInputs(),
    datalistId: DATALIST_ID,
  };

  constructor(
    private readonly root: HTMLElement,
    private readonly store: AppStore,
    private readonly cb: CalcCallbacks,
  ) {}

  mount(): void {
    this.ledger = el('div');
    this.verdict = el('div');
    mount(
      this.root,
      el('div', { class: 'desk' }, [
        el('section', { class: 'panel' }, [
          el('div', { class: 'panel-head' }, [el('h2', {}, '元帳'), el('span', { class: 'num' }, 'INPUT')]),
          this.ledger,
        ]),
        el('section', { class: 'panel' }, [
          el('div', { class: 'panel-head' }, [el('h2', {}, '判定'), el('span', { class: 'num' }, 'VERDICT')]),
          this.verdict,
        ]),
      ]),
    );
    this.store.subscribe(() => this.onStoreChange());
    this.renderInputs();
    this.renderOutput();
    this.lastInputsKey = this.inputsKey();
  }

  refreshInputs(): void {
    this.renderInputs();
  }

  private inputsKey(): string {
    return `${this.store.activeLeague}|${this.store.rateTable?.fetchedAt}|${this.store.currentSet.id}`;
  }

  private onStoreChange(): void {
    const key = this.inputsKey();
    if (key !== this.lastInputsKey) {
      this.lastInputsKey = key;
      this.renderInputs();
    }
    this.renderOutput();
  }

  // 総コスト・期待/最低収入（base, 決定的）。表の末尾サマリ用。
  private summary: { C: number; income: number; min: number } = { C: 0, income: 0, min: 0 };

  private computeSummary(): void {
    const rt = this.store.rateTable;
    if (!rt) {
      this.summary = { C: 0, income: 0, min: 0 };
      return;
    }
    const { input } = expandSet(this.store.currentSet, rt);
    this.summary = { C: input.C, income: expectedIncome(input), min: minIncome(input) };
  }

  // ── 入力（元帳） ──────────────────────
  private renderInputs(): void {
    this.computeSummary();
    this.dropCountInputs.clear();
    mount(
      this.ledger,
      this.buildGlobals(),
      this.buildSteadyTime(),
      this.buildCostTable(),
      this.buildDropTable(),
      this.buildBankrollBlock(),
      this.buildNotesBlock(),
      this.buildToolbar(),
      buildCatalogDatalist(this.store.rateTable, DATALIST_ID),
    );
  }

  // 自由記入メモ（マップ・タブレット・装備構成などの覚書）。計算には使わない。
  // oninput は updateSet 経由だが、署名（time/steady/cost/drops）に notes は含めないため
  // 再シミュは起きず、inputsKey も変わらないので再描画されず textarea のフォーカスは保たれる。
  private buildNotesBlock(): HTMLElement {
    const s = this.store.currentSet;
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, 'メモ（マップ・タブレット・構成など）'),
      // textarea の初期表示はテキスト内容（第3引数）で与える。value 属性は textarea に効かない。
      el('textarea', {
        class: 'notes-area',
        rows: '3',
        placeholder: '例：T15 マップ + 〇〇タブレット、〇〇のアトラスツリー、装備の制約 など自由に',
        oninput: (e) => this.store.updateSet(() => { s.notes = (e.target as HTMLTextAreaElement).value; }),
      }, s.notes ?? ''),
    ]);
  }

  // 相場の取得時刻・鮮度はここ1箇所だけに表示（§7）。
  private buildGlobals(): HTMLElement {
    const rt = this.store.rateTable;
    const leagueSel = el('select', {
      onchange: (e) => this.cb.onLeagueChange((e.target as HTMLSelectElement).value),
    }) as HTMLSelectElement;
    for (const l of this.store.leagues) {
      leagueSel.append(el('option', { value: l.Value, selected: l.Value === this.store.activeLeague }, l.IsCurrent ? `${l.Value}（現行）` : l.Value));
    }
    if (this.store.leagues.length === 0) leagueSel.append(el('option', {}, '取得中…'));

    const stale = rt ? (Date.now() - rt.fetchedAt) / 60000 > FRESHNESS_WARN_MINUTES : false;
    const freshLabel = rt
      ? el('span', { class: `freshness ${stale ? 'stale' : ''}` }, stale ? `相場が古い可能性（取得 ${clockHHMM(rt.fetchedAt)}）` : `相場 取得 ${clockHHMM(rt.fetchedAt)}`)
      : el('span', { class: 'freshness' }, '相場 取得中…');

    return el('div', { class: 'block globals-row' }, [
      el('div', { class: 'field' }, [el('label', {}, 'リーグ'), leagueSel]),
      el('div', { class: 'field' }, [
        el('label', {}, '表示単位'),
        unitSelect(this.store.rateTable, this.store.currentSet.displayUnit, (id) => { this.store.setDisplayUnit(id); this.syncBankrollField(); }),
      ]),
      el('div', { class: 'field' }, [
        el('label', {}, '相場'),
        el('div', { class: 'fresh-wrap' }, [freshLabel, el('button', { class: 'btn btn-ghost', onclick: () => this.cb.onRefetchRates() }, '↻ 再取得')]),
      ]),
    ]);
  }

  private buildSteadyTime(): HTMLElement {
    const s = this.store.currentSet;
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, '1周の基本'),
      el('div', { class: 'row-inline' }, [
        el('div', { class: 'field' }, [
          el('label', {}, '所要時間（秒）'),
          el('input', { type: 'number', value: String(s.time), min: '1', style: 'width:90px',
            oninput: (e) => this.store.updateSet(() => { s.time = num((e.target as HTMLInputElement).value, 1); }) }),
        ]),
        el('div', { class: 'field' }, [
          el('label', {}, '安定収入 / 周'),
          el('input', { type: 'number', value: String(s.steady.amount), style: 'width:90px',
            oninput: (e) => this.store.updateSet(() => { s.steady.amount = num((e.target as HTMLInputElement).value); }) }),
        ]),
        el('div', { class: 'field' }, [el('label', {}, '単位'), unitSelect(this.store.rateTable, s.steady.unit, (id) => this.store.updateSet(() => { s.steady.unit = id; }))]),
      ]),
    ]);
  }

  // ── コスト表 ──
  private buildCostTable(): HTMLElement {
    const s = this.store.currentSet;
    const body = el('tbody', {}, s.costLines.map((line) => this.itemRow(line, 'cost')));
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, 'コスト（毎周の消費）'),
      el('div', { class: 'tablewrap' }, [
        el('table', { class: 'ledger-table' }, [
          el('thead', {}, [el('tr', {}, [el('th', {}, 'アイテム'), el('th', {}, '価値'), el('th', {}, '単位'), el('th', { class: 'col-num' }, '個数'), el('th', { class: 'col-mini' })])]),
          body,
        ]),
      ]),
      el('button', { class: 'btn btn-add', onclick: () => { this.store.updateSet(() => s.costLines.push(createCostLine())); this.renderInputs(); } }, '＋ コスト'),
      this.summaryRow('総コスト / 周', [this.moneyChip(this.summary.C)]),
    ]);
  }

  // 表示単位で金額を表示する小チップ。
  private moneyChip(base: number, cls = ''): HTMLElement {
    const unit = this.store.currentSet.displayUnit;
    const rt = this.store.rateTable;
    const shown = rt ? fromBase(base, unit, rt) : null;
    return el('span', { class: `sum-chip ${cls}` }, `${fmtNum(shown)} ${unit}`);
  }

  private summaryRow(label: string, chips: HTMLElement[]): HTMLElement {
    return el('div', { class: 'sum-row' }, [el('span', { class: 'sum-label' }, label), el('div', { class: 'sum-chips' }, chips)]);
  }

  // ── ドロップ表 ──
  private buildDropTable(): HTMLElement {
    const s = this.store.currentSet;
    const body = el('tbody');
    for (const entry of s.dropLines) {
      if (isGroupDrop(entry)) continue;
      body.append(this.itemRow(entry, 'drop'));
      if (this.expanded.has(entry.id)) body.append(this.rateSubRow(entry));
    }
    const groups = s.dropLines.filter(isGroupDrop).map((g) => this.buildGroup(g));
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, 'ドロップ（当たり）'),
      el('div', { class: 'tablewrap' }, [
        el('table', { class: 'ledger-table' }, [
          el('thead', {}, [el('tr', {}, [el('th', {}, 'アイテム'), el('th', {}, '価値'), el('th', {}, '単位'), el('th', { class: 'col-num' }, '期待値λ'), el('th', { class: 'col-mini' }, '推定'), el('th', { class: 'col-mini' })])]),
          body,
        ]),
      ]),
      ...groups,
      el('div', { class: 'row-inline' }, [
        el('button', { class: 'btn btn-add', onclick: () => { this.store.updateSet(() => s.dropLines.push(createDropLine())); this.renderInputs(); } }, '＋ ドロップ'),
        el('button', { class: 'btn btn-add', onclick: () => { this.store.updateSet(() => s.dropLines.push(createGroupDrop())); this.renderInputs(); } }, '＋ グループ'),
      ]),
      this.summaryRow('総収入 / 周（安定収入込み）', [
        el('span', { class: 'sum-sub' }, '最低 '),
        this.moneyChip(this.summary.min, 'min'),
        el('span', { class: 'sum-sub' }, ' 期待 '),
        this.moneyChip(this.summary.income, 'exp'),
      ]),
    ]);
  }

  // アイテム行（cost / drop 共通の前半列）。
  private itemRow(row: CostLine | DropLine, role: 'cost' | 'drop'): HTMLElement {
    const remove = () => {
      this.store.updateSet(() => {
        const s = this.store.currentSet;
        if (role === 'cost') s.costLines = s.costLines.filter((l) => l.id !== row.id);
        else s.dropLines = s.dropLines.filter((l) => isGroupDrop(l) || l.id !== row.id);
      });
      this.renderInputs();
    };

    const cells: HTMLElement[] = [
      el('td', {}, [itemNameInput(row, this.picker)]),
      el('td', {}, [valueCell(row, this.picker)]),
      el('td', {}, [unitCell(row, this.picker)]),
    ];

    if (role === 'cost') {
      cells.push(el('td', { class: 'col-num' }, [
        el('input', { type: 'number', class: 'count', value: fmtNum(row.count), title: '消費個数 q',
          oninput: (e) => this.store.updateSet(() => { row.count = num((e.target as HTMLInputElement).value); }) }),
      ]));
    } else {
      const drop = row as DropLine;
      const empirical = drop.rateMode.kind !== 'direct';
      const countInput = el('input', { type: 'number', class: 'count', value: fmtNum(drop.count), disabled: empirical, title: '期待個数 λ',
        oninput: (e) => this.store.updateSet(() => { drop.count = num((e.target as HTMLInputElement).value); drop.rateMode = { kind: 'direct', lambda: drop.count }; }) }) as HTMLInputElement;
      this.dropCountInputs.set(drop.id, countInput);
      cells.push(el('td', { class: 'col-num' }, [countInput]));
      const open = this.expanded.has(drop.id);
      cells.push(el('td', { class: 'col-mini' }, [
        el('button', { class: `iconbtn tiny ${empirical ? 'on' : ''}`, title: 'λ 推定を編集（直接/実績/実績+予測）',
          onclick: () => { if (open) this.expanded.delete(drop.id); else this.expanded.add(drop.id); this.renderInputs(); } }, open ? '▾' : '▸'),
      ]));
    }
    cells.push(el('td', { class: 'col-mini' }, [el('button', { class: 'iconbtn danger tiny', title: '削除', onclick: remove }, '×')]));
    return el('tr', {}, cells);
  }

  // λ 推定パネル（畳んだ行の下に展開, §5.2）。
  private rateSubRow(drop: DropLine): HTMLElement {
    return el('tr', { class: 'rate-sub' }, [el('td', { colspan: '6' }, [this.buildRatePanel(drop)])]);
  }

  private buildRatePanel(drop: DropLine): HTMLElement {
    const mode = drop.rateMode.kind;
    const modeSel = el('select', {
      onchange: (e) => {
        const v = (e.target as HTMLSelectElement).value as RateMode['kind'];
        this.store.updateSet(() => {
          const prev = drop.count || 0.1;
          if (v === 'direct') drop.rateMode = { kind: 'direct', lambda: prev };
          else if (v === 'empirical') drop.rateMode = { kind: 'empirical', N: 100, k: 0 };
          else drop.rateMode = { kind: 'empirical_prior', priorRate: prev, strength: 'normal', N: 0, k: 0 };
          syncDropCount(drop);
        });
        this.renderInputs();
      },
    }) as HTMLSelectElement;
    for (const [v, label] of [['direct', '直接'], ['empirical', '実績'], ['empirical_prior', '実績+予測']] as const) {
      modeSel.append(el('option', { value: v, selected: v === mode }, label));
    }

    const body = el('div');
    if (drop.rateMode.kind !== 'direct') {
      const rm = drop.rateMode;
      // λ̂ 表示・粗さ注記は専用コンテナに分離。実績パラメタの入力中はここだけを差し替え、
      // 元帳 DOM 全体を作り直さない（＝入力欄のフォーカスを維持する）。
      const estBox = el('div');
      // ライブ更新: ストアを更新（→出力パネルは購読経由で再計算）しつつ、λ̂ 表示と
      // 親行の「期待値λ」の値だけをその場で書き換える。入力要素は作り直さない。
      const live = (mutate: () => void) => {
        this.store.updateSet(() => { mutate(); syncDropCount(drop); });
        this.renderEstimate(drop, estBox);
        const ci = this.dropCountInputs.get(drop.id);
        if (ci) ci.value = fmtNum(drop.count);
      };

      const fields: HTMLElement[] = [];
      if (rm.kind === 'empirical_prior') {
        fields.push(
          el('div', { class: 'field' }, [el('label', {}, '予測 λ₀'),
            el('input', { type: 'number', style: 'width:80px', value: String(rm.priorRate),
              oninput: (e) => live(() => { rm.priorRate = num((e.target as HTMLInputElement).value); }) })]),
          el('div', { class: 'field' }, [el('label', {}, '信頼度'), (() => {
            const sel = el('select', { onchange: (e) => live(() => { rm.strength = (e.target as HTMLSelectElement).value as Strength; }) }) as HTMLSelectElement;
            for (const [v, label] of [['weak', `弱め(${STRENGTH_M.weak})`], ['normal', `ふつう(${STRENGTH_M.normal})`], ['strong', `強め(${STRENGTH_M.strong})`]] as const) {
              sel.append(el('option', { value: v, selected: v === rm.strength }, label));
            }
            return sel;
          })()]),
        );
      }
      fields.push(
        el('div', { class: 'field' }, [el('label', {}, '周回数 N'),
          el('input', { type: 'number', style: 'width:80px', value: String(rm.N), oninput: (e) => live(() => { rm.N = num((e.target as HTMLInputElement).value); }) })]),
        el('div', { class: 'field' }, [el('label', {}, '観測 k'),
          el('input', { type: 'number', style: 'width:80px', value: String(rm.k), oninput: (e) => live(() => { rm.k = num((e.target as HTMLInputElement).value); }) })]),
      );
      body.append(el('div', { class: 'row-inline' }, fields));
      this.renderEstimate(drop, estBox);
      body.append(estBox);
    } else {
      body.append(el('p', { class: 'ci', style: 'margin:.2rem 0 0' }, '期待個数 λ を直接入力するモードです。実績周回から推定したい場合は「実績」を選んでください。'));
    }

    return el('div', { class: 'ratepanel' }, [el('div', { class: 'row-inline' }, [el('div', { class: 'field' }, [el('label', {}, 'λ 推定'), modeSel])]), body]);
  }

  // λ̂ 推定値・90%CI・粗さ注記を estBox に（再）描画。実績パラメタのライブ更新で
  // ここだけを差し替えるため、入力欄（兄弟要素）には触れずフォーカスが保たれる。
  private renderEstimate(drop: DropLine, box: HTMLElement): void {
    if (drop.rateMode.kind === 'direct') { mount(box); return; }
    const info = resolveRate(drop.rateMode);
    const children: HTMLElement[] = [
      el('div', {}, [
        el('span', { class: 'est' }, `λ̂ = ${fmtNum(info.lambdaHat)} `),
        el('span', { class: 'ci' }, info.ci90 ? `（90%CI ${fmtNum(info.ci90[0])}–${fmtNum(info.ci90[1])}）` : ''),
      ]),
    ];
    if (info.rough) {
      children.push(el('div', {}, [el('a', { class: 'rough', href: '#/explain/rate', title: '観測が薄いぶん安全側に補正されます' }, `📊 観測 k=${info.observedK} は少なく推定が粗い（相対誤差≈1/√k）`)]));
    }
    mount(box, ...children);
  }

  // グループドロップ（§5.2.2）。メンバーをミニ表で編集。
  private buildGroup(group: GroupDrop): HTMLElement {
    const s = this.store.currentSet;
    const removeGroup = () => { this.store.updateSet(() => { s.dropLines = s.dropLines.filter((d) => isGroupDrop(d) ? d.id !== group.id : true); }); this.renderInputs(); };

    // 確定 m 個を相対重み（比）で正規化配分: λ_i = m·比_i/Σ比。
    const totalW = group.members.reduce((acc, x) => acc + Math.max(0, x.p), 0);
    const memberBody = el('tbody', {}, group.members.map((mem) => {
      const lambda = totalW > 0 ? (group.m * Math.max(0, mem.p)) / totalW : 0;
      return el('tr', {}, [
        el('td', {}, [itemNameInput(mem, this.picker)]),
        el('td', {}, [valueCell(mem, this.picker)]),
        el('td', {}, [unitCell(mem, this.picker)]),
        el('td', { class: 'col-num' }, [el('input', { type: 'number', class: 'count', value: String(mem.p), title: '相対重み（比）',
          oninput: (e) => this.store.updateSet(() => { mem.p = num((e.target as HTMLInputElement).value); }) })]),
        el('td', { class: 'col-lam' }, [el('span', { class: 'lam-hint', title: 'このメンバーの期待個数 λ_i = m·比_i/Σ比' }, `λ ${fmtNum(lambda)}`)]),
        el('td', { class: 'col-mini' }, [el('button', { class: 'iconbtn danger tiny', title: 'メンバー削除',
          onclick: () => { this.store.updateSet(() => { group.members = group.members.filter((x) => x.id !== mem.id); }); this.renderInputs(); } }, '×')]),
      ]);
    }));

    return el('div', { class: 'group-card' }, [
      el('div', { class: 'group-head' }, [
        el('span', { class: 'badge badge-mode' }, 'グループ'),
        el('input', { type: 'text', class: 'item-name grow', value: group.name, placeholder: 'グループ名（ボス報酬等）',
          oninput: (e) => this.store.updateSet(() => { group.name = (e.target as HTMLInputElement).value; }) }),
        el('label', { class: 'm-label' }, '確定 m'),
        el('input', { type: 'number', class: 'count', value: String(group.m), min: '0',
          oninput: (e) => this.store.updateSet(() => { group.m = num((e.target as HTMLInputElement).value); }) }),
        el('button', { class: 'iconbtn danger tiny', title: 'グループ削除', onclick: removeGroup }, '×'),
      ]),
      el('div', { class: 'tablewrap' }, [
        el('table', { class: 'ledger-table sub' }, [
          el('thead', {}, [el('tr', {}, [el('th', {}, 'メンバー'), el('th', {}, '価値'), el('th', {}, '単位'), el('th', { class: 'col-num' }, '比'), el('th', { class: 'col-lam' }, '→ λ'), el('th', { class: 'col-mini' })])]),
          memberBody,
        ]),
      ]),
      el('div', { class: 'group-foot' }, [
        el('button', { class: 'btn btn-add', onclick: () => { this.store.updateSet(() => group.members.push(createGroupDrop().members[0]!)); this.renderInputs(); } }, '＋ メンバー'),
        el('span', { class: 'ci' }, '確定 m 個を「比」で配分（合計 λ = m）。比は合計1でなくてOK・相対値で入力。'),
      ]),
    ]);
  }

  private buildBankrollBlock(): HTMLElement {
    const s = this.store.currentSet;
    const rt = this.store.rateTable;
    const shown = rt && s.currentBankroll != null ? fromBase(s.currentBankroll, s.displayUnit, rt) : null;
    const input = el('input', { type: 'number', style: 'width:140px', value: shown == null ? '' : fmtNum(shown), placeholder: '任意',
      oninput: (e) => {
        const raw = (e.target as HTMLInputElement).value;
        if (raw === '') { this.store.setCurrentBankroll(undefined); return; }
        const base = rt ? toBase(num(raw), s.displayUnit, rt) : null;
        this.store.setCurrentBankroll(base ?? undefined);
      } }) as HTMLInputElement;
    this.bankrollInput = input;
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, `現在の軍資金（${s.displayUnit}・判定用）`),
      el('div', { class: 'row-inline' }, [el('div', { class: 'field' }, [el('label', {}, '手元資金'), input])]),
    ]);
  }

  private syncBankrollField(): void {
    const s = this.store.currentSet;
    const rt = this.store.rateTable;
    if (!this.bankrollInput) return;
    const shown = rt && s.currentBankroll != null ? fromBase(s.currentBankroll, s.displayUnit, rt) : null;
    this.bankrollInput.value = shown == null ? '' : fmtNum(shown);
  }

  private buildToolbar(): HTMLElement {
    const s = this.store.currentSet;
    const loadSel = el('select', { onchange: (e) => { const v = (e.target as HTMLSelectElement).value; if (v) this.cb.onLoad(v); } }) as HTMLSelectElement;
    loadSel.append(el('option', { value: '' }, '保存済みを開く…'));
    for (const set of this.cb.savedSets()) loadSel.append(el('option', { value: set.id, selected: set.id === s.id }, set.name));
    const fileInput = el('input', { type: 'file', accept: 'application/json', style: 'display:none',
      onchange: (e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) this.cb.onImport(f); } }) as HTMLInputElement;
    return el('div', { class: 'toolbar' }, [
      el('input', { type: 'text', class: 'grow', value: s.name, placeholder: 'セット名',
        oninput: (e) => this.store.updateSet(() => { s.name = (e.target as HTMLInputElement).value; }) }),
      el('button', { class: 'btn btn-primary', onclick: () => this.cb.onSave() }, '保存'),
      loadSel,
      el('button', { class: 'btn', onclick: () => this.cb.onNew() }, '新規'),
      el('button', { class: 'btn', onclick: () => this.cb.onDuplicate() }, '複製'),
      el('button', { class: 'btn', onclick: () => this.cb.onDelete() }, '削除'),
      el('button', { class: 'btn btn-ghost', onclick: () => this.cb.onExport() }, '⤓ JSON'),
      el('button', { class: 'btn btn-ghost', onclick: () => fileInput.click() }, '⤒ 取込'),
      fileInput,
    ]);
  }

  // ── 出力（判定） ──────────────────────
  private renderOutput(): void {
    const { rateTable, compute, currentSet } = this.store;
    if (!rateTable) {
      mount(this.verdict, el('div', { class: 'placeholder' }, '相場を取得しています…'));
      return;
    }
    const { result, warnings, computing } = compute;
    if (!result) {
      mount(this.verdict, el('div', { class: 'placeholder' }, computing ? '計算中…' : 'コストとドロップを入力してください。'));
      return;
    }
    const unit = currentSet.displayUnit;

    if (!result.valid) {
      const msg = result.reason === 'ev'
        ? 'EV ≤ 0：資金管理しても理論上は溶けます。期待利益がプラスになる構成にしましょう。'
        : result.reason === 'cost'
          ? 'コストを入力してください。'
          : result.reason === 'rate'
            ? 'コスト行に相場未取得のアイテムがあります。行を手動価格（✎）に切替えるか、相場を再取得してください（コストが不完全だと期待値が過大に出るため計算を止めています）。'
            : 'ドロップ行を1つ以上追加してください（安定収入だけなら破産リスクはほぼ無く、軍資金は実質コストのみ）。';
      mount(this.verdict, this.warnList(warnings), el('div', { class: 'verdict danger' }, [el('b', {}, '推奨軍資金なし'), el('p', { style: 'margin:.4rem 0 0' }, msg)]));
      return;
    }

    const d = toDisplayUnit(result, currentSet.time, unit, rateTable);
    const B = currentSet.currentBankroll;
    const currentDisp = B != null ? fromBase(B, unit, rateTable) : null;

    // 最低収入がコストを上回る＝どんなに運が悪くても毎周プラス → 破産不能。ケリーは無意味なので特別表示。
    const guaranteed = result.minIncome >= result.C - 1e-9;
    const costDisp = fromBase(result.C, unit, rateTable);

    let hero: HTMLElement;
    let gates: HTMLElement | null;
    let v = judge(result.bankFull, result.bankHalf, result.bankQuarter, result.C, B);

    if (guaranteed) {
      hero = el('div', { class: 'hero guaranteed' }, [
        el('span', { class: 'eyebrow safe' }, '💰 破産リスクなし'),
        el('div', { class: 'figure small' }, [document.createTextNode(fmtNum(costDisp)), el('span', { class: 'u' }, unit)]),
        el('p', { class: 'guar-note' }, '最低収入がコストを上回るため、どんなに運が悪くても毎周プラス。手元に1周分のコスト（上記）さえあれば破産しません。ケリー基準の段階分けは適用外です。'),
        el('div', { class: 'sub' }, [
          el('span', {}, ['EV/周 ', el('b', {}, fmtMoney(d.EV, unit))]),
          el('span', {}, ['最低収入/周 ', el('b', {}, fmtMoney(d.minIncome, unit))]),
        ]),
      ]);
      gates = null;
      // 判定は B とコストの比較のみ（攻め/標準などのゾーンは無い）。
      v = B == null
        ? { zone: 'neutral', activeTier: null, text: '現在の軍資金を入れると、コストを賄えるか判定します。' }
        : B < result.C
          ? { zone: 'danger', activeTier: null, text: 'コスト1周分に届いていません。まずはコスト分の元手を用意しましょう。' }
          : { zone: 'safe', activeTier: null, text: 'コスト1周分以上あります。破産せず回せます。' };
    } else {
      hero = el('div', { class: 'hero' }, [
        el('span', { class: 'eyebrow' }, '推奨軍資金（ハーフケリー）'),
        el('div', { class: 'figure' }, [document.createTextNode(fmtNum(d.bankHalf)), el('span', { class: 'u' }, unit)]),
        el('div', { class: 'sub' }, [
          el('span', {}, ['破産確率 ', el('b', {}, pct(result.ruinHalf))]),
          el('span', {}, ['EV/周 ', el('b', {}, fmtMoney(d.EV, unit))]),
        ]),
      ]);
      const gate = (cls: string, tag: string, amt: number | null, ruin: number, active: boolean) =>
        el('div', { class: `gate ${cls} ${active ? 'active' : ''}` }, [el('div', { class: 'tag' }, tag), el('div', { class: 'amt' }, fmtNum(amt)), el('div', { class: 'ruin' }, `破産 ${pct(ruin)}`)]);
      gates = el('div', { class: 'gates' }, [
        gate('full', '攻め・フル', d.bankFull, result.ruinFull, v.activeTier === 'full'),
        gate('half', '標準・ハーフ', d.bankHalf, result.ruinHalf, v.activeTier === 'half'),
        gate('quarter', '慎重・クォーター', d.bankQuarter, result.ruinQuarter, v.activeTier === 'quarter'),
      ]);
    }

    const metric = (k: string, val: string, opts: { hl?: boolean; neg?: boolean } = {}) =>
      el('div', { class: `metric ${opts.hl ? 'hl' : ''}` }, [el('div', { class: 'k' }, k), el('div', { class: `v ${opts.neg ? 'neg' : ''}` }, val)]);
    const metrics = el('div', { class: 'metrics' }, [
      metric('期待収入/周', fmtMoney(d.income, unit)),
      metric('EV/周', fmtMoney(d.EV, unit), { neg: (d.EV ?? 0) < 0 }),
      metric('ROI', pct(d.roi)),
      metric('ケリー割合 f*', pct(d.fStar)),
      metric('伸び/周', pct(d.growthHalf, 2)),
      metric('期待時給', fmtMoney(d.expectedHourly, unit), { hl: true }),
      metric('最低時給', fmtMoney(d.minHourly, unit), { neg: (d.minHourly ?? 0) < 0 }),
    ]);

    const canvas = el('canvas') as HTMLCanvasElement;
    const note = this.thinDataNote();
    const verdictBox = el('div', { class: `verdict ${v.zone}` }, [el('b', {}, '判定'), document.createTextNode('：'), document.createTextNode(v.text)]);

    mount(this.verdict, this.warnList(warnings), hero, note ?? el('span'), gates, metrics, el('div', { class: 'chartwrap' }, [canvas]), verdictBox);

    this.chart?.destroy();
    this.chart = new RuinChart(canvas);
    this.chart.update(d.curve, { full: d.bankFull ?? 0, half: d.bankHalf ?? 0, quarter: d.bankQuarter ?? 0, current: currentDisp }, unit);
  }

  private warnList(warnings: string[]): HTMLElement | false {
    if (warnings.length === 0) return false;
    return el('ul', { class: 'warns' }, warnings.map((w) => el('li', {}, w)));
  }

  private thinDataNote(): HTMLElement | null {
    const thin = this.store.currentSet.dropLines.some((d) => {
      if (isGroupDrop(d)) return d.members.some((m) => m.rateMode && resolveRate(m.rateMode).rough);
      const info = resolveRate(d.rateMode);
      return d.rateMode.kind !== 'direct' && (info.observedK ?? 0) < ROUGH_K_THRESHOLD;
    });
    if (!thin) return null;
    return el('a', { class: 'note-soft', href: '#/explain/uncertainty' }, '📊 推定が粗い行があるため、安全側（軍資金多め）に補正しています');
  }
}
